/**
 * Deterministic zodiac compatibility scoring.
 * Combines element affinity, modality dynamics, and ZODIAC_EXTRAS' compatible/challenging
 * lists to produce stable scores for all 144 zodiac pair pages.
 */
import {
  ALL_WESTERN_ZODIACS,
  ZODIAC_EXTRAS,
  type WesternZodiac,
  zodiacSlug,
  getZodiacBySlug,
} from "./zodiac";

export interface PairScore {
  /** 0-100 overall. */
  overall: number;
  love: number;
  friend: number;
  work: number;
  /** Short ID copy summarising the dynamic. */
  summaryId: string;
  /** Strength bullets in ID. */
  strengths: string[];
  /** Watch-out bullets in ID. */
  watchOuts: string[];
}

const ELEMENT_PAIR: Record<string, Record<string, number>> = {
  Api:   { Api: 80, Udara: 90, Tanah: 55, Air: 50 },
  Udara: { Udara: 78, Api: 90, Air: 60, Tanah: 55 },
  Tanah: { Tanah: 82, Air: 88, Api: 55, Udara: 55 },
  Air:   { Air: 80, Tanah: 88, Udara: 60, Api: 50 },
};

function modalityBoost(a: string, b: string): number {
  if (a === b) return -3; // same modality often clashes
  return 4; // different modalities complement
}

function clamp(n: number, lo = 30, hi = 99): number {
  return Math.max(lo, Math.min(hi, Math.round(n)));
}

/**
 * Compute a deterministic compatibility score for two signs (order-insensitive).
 */
export function computePair(a: WesternZodiac, b: WesternZodiac): PairScore {
  const xa = ZODIAC_EXTRAS[a.id];
  const xb = ZODIAC_EXTRAS[b.id];
  const base = ELEMENT_PAIR[a.elementId][b.elementId];
  const mod = modalityBoost(xa.modality, xb.modality);
  const compatBonus =
    (xa.compatible.includes(b.id) ? 6 : 0) + (xb.compatible.includes(a.id) ? 6 : 0);
  const challengePenalty =
    (xa.challenging.includes(b.id) ? 8 : 0) + (xb.challenging.includes(a.id) ? 8 : 0);
  const same = a.id === b.id ? -2 : 0;

  const overall = clamp(base + mod + compatBonus - challengePenalty + same);
  // love leans on element + compat list; work leans on modality + element; friend balances.
  const love = clamp(base + compatBonus - challengePenalty * 0.8 + (a.elementId === b.elementId ? 3 : 0));
  const work = clamp(base + mod * 1.2 + (xa.modality === "Cardinal" || xb.modality === "Cardinal" ? 3 : 0) - challengePenalty * 0.5);
  const friend = clamp((overall + love + work) / 3 + 2);

  const isHarmonious = a.elementId === b.elementId || ELEMENT_PAIR[a.elementId][b.elementId] >= 80;
  const summaryId = isHarmonious
    ? `${a.nameId} dan ${b.nameId} membawa energi yang saling melengkapi — kombinasi elemen ${a.elementId} & ${b.elementId} menumbuhkan rasa nyaman dan stabil.`
    : `${a.nameId} dan ${b.nameId} adalah pasangan kontras: gesekan elemen ${a.elementId} dengan ${b.elementId} bisa jadi pemicu pertumbuhan jika dikelola dewasa.`;

  const strengths = [
    `Elemen ${a.elementId} (${a.nameId}) bertemu ${b.elementId} (${b.nameId}) menciptakan dinamika ${isHarmonious ? "harmonis" : "saling mengisi"}.`,
    `Sifat ${a.traitsId[0]} ${a.nameId} mendukung sisi ${b.traitsId[1] ?? b.traitsId[0]} ${b.nameId}.`,
    `Modalitas ${xa.modality} & ${xb.modality} ${xa.modality === xb.modality ? "membuat ritme sejalan" : "saling melengkapi dalam pengambilan keputusan"}.`,
  ];
  const watchOuts = [
    `Hindari ego saat sama-sama ${a.traitsId[2] ?? a.traitsId[0]}.`,
    challengePenalty > 0
      ? `Tradisi astrologi mencatat ${a.nameId} dan ${b.nameId} kerap berbenturan pada urusan kontrol — komunikasi terbuka jadi kunci.`
      : `Perbedaan tempo bisa muncul; sepakati ekspektasi sejak awal.`,
    `Beri ruang untuk sifat ${b.traitsId[3] ?? b.traitsId[0]} ${b.nameId} agar tidak merasa tertekan.`,
  ];
  return { overall, love, friend, work, summaryId, strengths, watchOuts };
}

/** Canonical pair slug, e.g. "aries-dan-leo" — alphabetical order ensures one canonical URL per pair. */
export function pairSlug(a: WesternZodiac, b: WesternZodiac): string {
  const [s1, s2] = [zodiacSlug(a), zodiacSlug(b)].sort();
  return `${s1}-dan-${s2}`;
}

/** Parse "aries-dan-leo" or "aries-leo" into the two signs (any order). */
export function parsePairSlug(slug: string): { a: WesternZodiac; b: WesternZodiac } | null {
  const norm = slug.toLowerCase();
  const m = norm.match(/^([a-z]+)[-_](?:dan|and|x|vs|&)?[-_]?([a-z]+)$/);
  if (!m) return null;
  const a = getZodiacBySlug(m[1]);
  const b = getZodiacBySlug(m[2]);
  if (!a || !b) return null;
  return { a, b };
}

/** All 144 ordered pairs as canonical (alphabetical) slugs — deduplicated to 78. */
export function allPairSlugs(): { slug: string; a: WesternZodiac; b: WesternZodiac }[] {
  const out: { slug: string; a: WesternZodiac; b: WesternZodiac }[] = [];
  const seen = new Set<string>();
  for (const a of ALL_WESTERN_ZODIACS) {
    for (const b of ALL_WESTERN_ZODIACS) {
      const slug = pairSlug(a, b);
      if (seen.has(slug)) continue;
      seen.add(slug);
      // Use canonical alphabetical order for a/b too
      const ordered = [a, b].sort((x, y) => x.id.localeCompare(y.id));
      out.push({ slug, a: ordered[0], b: ordered[1] });
    }
  }
  return out;
}